import { useCallback, useState } from "react";

interface UsePhotoCaptureOptions {
  onPhotoCapture: (file: File) => void;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

interface UsePhotoCaptureReturn {
  capturePhoto: () => Promise<void>;
  isSupported: boolean;
}

export const usePhotoCapture = ({
  onPhotoCapture,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.8,
}: UsePhotoCaptureOptions): UsePhotoCaptureReturn => {
  const [isSupported] = useState(() => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  });

  const capturePhoto = useCallback(async () => {
    if (!isSupported) {
      throw new Error("Camera not supported");
    }

    try {
      // Get user media for camera with enhanced settings
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: maxWidth },
          height: { ideal: maxHeight },
          facingMode: "environment", // Use back camera on mobile
          // Request higher frame rate for better auto-adjustment
          frameRate: { ideal: 30, min: 15 },
        },
      });

      // Apply advanced camera settings if supported
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack?.applyConstraints) {
        try {
          await videoTrack.applyConstraints({
            advanced: [
              {
                exposureMode: "continuous",
                whiteBalanceMode: "continuous",
                focusMode: "continuous",
              } as any,
            ], // Type assertion for experimental features
          });
        } catch (error) {
          console.log("Advanced camera constraints not supported:", error);
        }
      }

      // Create video element
      const video = document.createElement("video");
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;

      // Wait for video to be ready and allow camera to adjust
      await new Promise((resolve) => {
        video.onloadedmetadata = resolve;
      });

      // Give camera additional time to adjust exposure and focus (2 seconds)
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Create canvas and capture frame
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d")!;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      context.drawImage(video, 0, 0);

      // Stop the stream
      stream.getTracks().forEach((track) => track.stop());

      // Convert to blob and create file
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `delivery_photo_${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            onPhotoCapture(file);
          }
        },
        "image/jpeg",
        quality
      );
    } catch (error) {
      console.error("Error capturing photo:", error);
      throw error;
    }
  }, [isSupported, onPhotoCapture, maxWidth, maxHeight, quality]);

  return {
    capturePhoto,
    isSupported,
  };
};
